import { z } from "zod";
import type { Operation } from "../types";

// Account contract — the thin "who is using the product" signal.
//
// Excellent is local-first and otherwise emits only ANONYMOUS telemetry (email
// is on an absolute denylist in the scrubber). This is the ONE deliberately
// IDENTIFIED, opt-in signal: when a user finishes first-run registration they
// may share an email so the vendor can reach them and knows who's using the app.
//
// The browser posts the record here; the server-side route forwards it to the
// central sink (the marketing site's `/api/users`, which writes the dedicated
// Supabase `ex_users` table). The Supabase secret stays on that server — it is
// NEVER shipped in the distributed app. Opt-in + disclosed at the field; see
// excellent/app/login/_client.tsx.

const notifySignupBody = z.object({
  email: z.string().email().max(254),
  // The name + handle the user already typed at registration — so the central
  // record is "who", not just an address.
  name: z.string().max(120).optional(),
  handle: z.string().max(64).optional(),
  // Where the sign-up happened (defaults to "app-signup" server-side).
  source: z.string().max(64).optional(),
  // Open bag for any extra data we decide to collect later, without a new
  // contract — lands in the `ex_users.meta` jsonb column. Bounded so neither this
  // path nor the public sink can be turned into a storage-amplification vector
  // (the sink re-checks this independently).
  meta: z
    .record(z.string(), z.unknown())
    .refine((m) => JSON.stringify(m).length <= 4096, { message: "meta too large" })
    .optional(),
});
type NotifySignupBody = z.infer<typeof notifySignupBody>;

const okResponse = z.object({ ok: z.boolean() });
type OkResponse = z.infer<typeof okResponse>;

// Recovery-code backup email. At first-run registration the app issues one-time
// recovery codes (the only way back into a local, passkey-secured account after a
// lost device). When the user shared an email, they may also email themselves a
// backup of those codes. The browser posts here; the server-side relay forwards
// to the central mailer (the site's /api/recovery-email) — the codes never touch
// the Supabase secret or any vendor store, only the transactional email path.
const recoveryEmailBody = z.object({
  email: z.string().email().max(254),
  codes: z.array(z.string().min(1).max(64)).min(1).max(24),
  handle: z.string().max(64).optional(),
});
type RecoveryEmailBody = z.infer<typeof recoveryEmailBody>;

// Best-effort: a sink failure must NEVER break sign-in, so the caller fires this
// and ignores the result. The route still reports honestly.
const notifySignup: Operation<void, void, NotifySignupBody, OkResponse> = {
  method: "POST",
  path: () => "/api/account/notify-signup",
  body: notifySignupBody,
  response: okResponse,
};

// Best-effort: a mailer failure must not block sign-in; the UI tells the user to
// save the codes manually if it didn't go out.
const recoveryEmail: Operation<void, void, RecoveryEmailBody, OkResponse> = {
  method: "POST",
  path: () => "/api/account/recovery-email",
  body: recoveryEmailBody,
  response: okResponse,
};

export const account = {
  notifySignup,
  recoveryEmail,
};
